import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CreditCard, Banknote } from "lucide-react";
import { toast } from "sonner";
import { api, inr, formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function Checkout() {
  const { items, total, clearCart } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", address: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const validate = () => {
    if (form.phone.replace(/\D/g, "").length < 10) {
      setError("Please enter a valid 10-digit phone number.");
      return false;
    }
    return true;
  };

  const placeOrderCOD = async (e) => {
    e.preventDefault();
    setError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        customer: form,
        items: items.map((i) => ({ product_id: i.product.id, qty: i.qty })),
      });
      clearCart();
      toast.success("Order placed successfully!");
      navigate(`/order-success/${data.id}`);
    } catch (err) {
      setError(formatApiError(err, "Could not place order. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const placeOrderOnline = async () => {
    setError("");
    if (!form.name.trim() || !form.address.trim()) {
      setError("Please fill in your name and address before paying online.");
      return;
    }
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data: order } = await api.post("/orders", {
        customer: form,
        items: items.map((i) => ({ product_id: i.product.id, qty: i.qty })),
      });
      const { data: rp } = await api.post("/payments/create-order", { order_id: order.id });
      const scriptOk = await loadRazorpayScript();
      if (!scriptOk) throw new Error("script");
      const rzp = new window.Razorpay({
        key: rp.key_id,
        amount: rp.amount,
        currency: rp.currency,
        name: "Jehovah Nissi Solar Agency",
        description: `Order #${order.id.slice(0, 8)}`,
        order_id: rp.razorpay_order_id,
        prefill: { name: form.name, contact: form.phone.replace(/\D/g, "").slice(-10) },
        theme: { color: "#EA580C" },
        handler: async (res) => {
          try {
            await api.post("/payments/verify", {
              order_id: order.id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            clearCart();
            toast.success("Payment successful!");
            navigate(`/order-success/${order.id}`);
          } catch {
            toast.error("Payment verification failed. We will call you to confirm your order.");
            clearCart();
            navigate(`/order-success/${order.id}`);
          }
        },
        modal: {
          ondismiss: () => {
            toast.info("Payment cancelled — your order is saved. You can pay on delivery.");
            clearCart();
            navigate(`/order-success/${order.id}`);
          },
        },
      });
      rzp.on("payment.failed", () => toast.error("Payment failed. Please try again or choose Pay on Delivery."));
      rzp.open();
    } catch (err) {
      setError(formatApiError(err, "Could not start online payment. Please use Pay on Delivery."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="min-h-screen flex flex-col">
      <Header />
      <main className="max-w-7xl mx-auto px-6 py-12 w-full flex-1">
        <Link to="/" data-testid="back-to-shop-link" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors duration-200 mb-8">
          <ArrowLeft className="w-4 h-4" /> Continue Shopping
        </Link>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mb-10">Checkout</h1>

        {items.length === 0 ? (
          <div data-testid="checkout-empty" className="bg-white border border-border rounded-md p-12 text-center">
            <p className="text-muted-foreground">Your cart is empty.</p>
            <Link to="/" className="text-primary font-medium text-sm mt-2 inline-block hover:underline">Browse products</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <form data-testid="checkout-form" onSubmit={placeOrderCOD} className="lg:col-span-7 bg-white border border-border rounded-md p-8 space-y-6">
              <div>
                <p className="font-heading font-semibold text-lg mb-1">Delivery Details</p>
                <p className="text-sm text-muted-foreground">Pay online now or on delivery. Our team will call to confirm your order and schedule installation.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input id="name" data-testid="checkout-name-input" required minLength={2} value={form.name} onChange={update("name")} placeholder="e.g. Rajesh Kumar" className="focus:ring-2 focus:ring-primary" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input id="phone" data-testid="checkout-phone-input" required type="tel" value={form.phone} onChange={update("phone")} placeholder="e.g. 98765 43210" className="focus:ring-2 focus:ring-primary" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Full Delivery Address</Label>
                <Textarea id="address" data-testid="checkout-address-input" required minLength={10} rows={4} value={form.address} onChange={update("address")} placeholder="Door no, street, area, city, PIN code" className="focus:ring-2 focus:ring-primary" />
              </div>
              {error && <p data-testid="checkout-error" className="text-sm text-destructive">{error}</p>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Button data-testid="pay-online-button" type="button" onClick={placeOrderOnline} disabled={submitting} className="w-full rounded-full bg-secondary hover:bg-secondary/90 text-white h-12 text-base active:scale-[0.98] transition-[background-color,transform] duration-200">
                  <CreditCard className="w-4 h-4 mr-2" /> {submitting ? "Please wait..." : `Pay Online \u2022 ${inr(total)}`}
                </Button>
                <Button data-testid="place-order-button" type="submit" disabled={submitting} variant="outline" className="w-full rounded-full border-primary text-primary hover:bg-primary hover:text-white h-12 text-base active:scale-[0.98] transition-[background-color,color,transform] duration-200">
                  <Banknote className="w-4 h-4 mr-2" /> Pay on Delivery
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Online payments are processed securely by Razorpay (UPI, cards, netbanking, wallets).</p>
            </form>

            <aside data-testid="order-summary" className="lg:col-span-5">
              <div className="bg-white border border-border rounded-md p-8 sticky top-24">
                <p className="font-heading font-semibold text-lg mb-6">Order Summary</p>
                <div className="space-y-4">
                  {items.map(({ product, qty }) => (
                    <div key={product.id} data-testid={`summary-item-${product.id}`} className="flex items-center gap-4">
                      <img src={product.image} alt={product.name} className="w-12 h-12 rounded-md object-cover" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{product.name}</p>
                        <p className="text-xs text-muted-foreground">Qty {qty}</p>
                      </div>
                      <span className="text-sm font-semibold">{inr(product.price * qty)}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-border mt-6 pt-4 space-y-2 text-sm">
                  <div className="flex justify-between text-muted-foreground"><span>Delivery &amp; Installation</span><span>Free in Trichy</span></div>
                  <div className="flex justify-between font-heading font-bold text-lg"><span>Total</span><span data-testid="summary-total" className="text-primary">{inr(total)}</span></div>
                </div>
              </div>
            </aside>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
